import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar } from 'r/ui';
import type { ScoreMarket } from 'r/types';
import {
  getMarketPredictions,
  getMarketSymbol,
  isMarketStarted,
} from 'r/utils/scoreMarket';
import { Button } from '@/components/ui/button';
import { useDeleteScoreMarket } from './mutations';

const statusConfig = {
  notStarted: {
    label: 'Not Started',
    className: 'bg-gray-500/10 text-gray-500 border border-gray-500/20',
  },
  open: {
    label: 'Live',
    className: 'bg-green-500/10 text-green-500 border border-green-500/20',
  },
  voting_closed: {
    label: 'Voting closed',
    className: 'bg-blue-500/10 text-blue-500 border border-blue-500/20',
  },
  resolved: {
    label: 'Resolved',
    className: 'bg-red-500/10 text-red-500 border border-red-500/20',
  },
} as const;

const formatDate = (date: string) =>
  new Date(date).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

interface ScoreMarketCardProps {
  market: ScoreMarket;
  expandedTextId: string | null;
  onToggleExpand: (id: string | null) => void;
}

export default React.memo(function ScoreMarketCard({
  market,
  expandedTextId,
  onToggleExpand,
}: ScoreMarketCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { mutate: deleteMarket, isPending: isDeleting } =
    useDeleteScoreMarket();

  const isStarted = isMarketStarted(market);
  const status =
    isStarted ? statusConfig[market.marketStatus] : statusConfig.notStarted;
  const predictions = getMarketPredictions(market);
  const description = market.description ?? '';

  return (
    <div className="bg-main rounded-xl border border-bg-accent-tabs p-5 flex flex-col gap-4 hover:border-text-tertiary transition-colors">
      {/* Author Info */}
      <div className="flex items-center gap-3">
        <Avatar
          size={40}
          user={{ id: market.kol.handle, avatar: market.kol.avatarUrl }}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-main font-semibold truncate">
            {market.kol.name}
          </span>
          <span className="text-secondary font-normal text-xs truncate">
            @{market.kol.handle}
          </span>
        </div>
      </div>

      {/* Card Header with Status Badge */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="flex-1 min-w-0 text-base font-semibold text-main truncate">
          {market.title}
        </h3>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-secondary font-medium">
            {getMarketSymbol(market)} ·{' '}
            {market.direction === 'up' ? 'Up' : 'Down'}
          </span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${status.className}`}
          >
            {status.label}
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="text-sm text-main leading-relaxed whitespace-pre-wrap">
        {description.length > 150 ?
          <span>
            {expandedTextId === market.id ?
              description
            : description.substring(0, 150) + '...'}
            <button
              onClick={() =>
                onToggleExpand(expandedTextId === market.id ? null : market.id)
              }
              className="text-focus ml-1 font-medium cursor-pointer hover:underline"
            >
              {expandedTextId === market.id ? 'Show less' : 'Show more'}
            </button>
          </span>
        : description}
      </div>

      {/* Dates and predictions */}
      <div className="flex flex-col gap-1 text-xs text-secondary">
        <span>Start: {formatDate(market.startedAt)}</span>
        <span>Entrance deadline: {formatDate(market.votingEndsAt)}</span>
        <span>Resolves: {formatDate(market.resolvesAt)}</span>
        <span className={predictions > 0 ? 'text-main font-medium' : undefined}>
          {predictions > 0 ?
            `${predictions} prediction${predictions > 1 ? 's' : ''}`
          : 'No predictions'}
        </span>
      </div>

      {/* Card Footer with Controls */}
      <div className="mt-auto pt-4 border-t border-bg-accent-tabs grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate(`/score-markets/${market.id}/edit`, {
              state: { background: location },
            })
          }
          className="text-xs px-3 py-2"
        >
          <span className="m-auto">Edit</span>
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            if (!isConfirmingDelete) return setIsConfirmingDelete(true);
            deleteMarket(market.id);
          }}
          onBlur={() => setIsConfirmingDelete(false)}
          disabled={isStarted || isDeleting}
          title={isStarted ? 'Started markets cannot be deleted' : undefined}
        >
          <span className="m-auto">
            {isConfirmingDelete ? 'Confirm delete' : 'Delete'}
          </span>
        </Button>
      </div>
    </div>
  );
});
